import { type FC, useId, Suspense, useState, useEffect } from 'react';
import { graphql, useFragment, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import classnames from 'classnames';

import { defineMessages, useIntl } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import AlertBellIcon from 'dibs-icons/exports/legacy/AlertBell.ts';
import { userNotificationsQuery } from './UserNotifications.tsx';

import { useGetAllUnviewedNotifications } from './useGetAllUnviewedNotifications.ts';

import { type UserNotificationsQuery } from './__generated__/UserNotificationsQuery.graphql.ts';
import { type UserNotificationsIcon_viewer$key } from './__generated__/UserNotificationsIcon_viewer.graphql.ts';

import { trackUserNotifications } from './userNotificationsTracking.ts';

import styles from './UserNotificationsIcon.scss';
import dibsCss from 'dibs-css';

const messages = defineMessages({
    label: {
        id: 'dbl.Header.UserNotificationsIcon.label',
        defaultMessage: 'Notifications',
    },
    description: {
        id: 'dbl.Header.UserNotificationsIcon.description',
        defaultMessage: 'Unread messages',
    },
});

const MAX_NOTIFICATIONS_COUNT = 20;

const viewerFragment = graphql`
    fragment UserNotificationsIcon_viewer on Viewer {
        userNotifications(userId: $userId) {
            ...useGetAllUnviewedNotifications_userNotifications
        }
    }
`;

const NotificationsBadge: FC<{
    queryRef: PreloadedQuery<UserNotificationsQuery>;
    isMobile?: boolean;
    setHasUnread: (hasUnread: boolean) => void;
}> = ({ queryRef, isMobile, setHasUnread }) => {
    const data = usePreloadedQuery<UserNotificationsQuery>(userNotificationsQuery, queryRef);
    const viewer = useFragment<UserNotificationsIcon_viewer$key>(viewerFragment, data.viewer);

    const { unviewedNotificationsCount } = useGetAllUnviewedNotifications({
        userNotifications: viewer?.userNotifications ?? null,
    });

    const notificationsCount = unviewedNotificationsCount;
    const hasUnreadNotifications = notificationsCount > 0;

    useEffect(() => {
        setHasUnread(hasUnreadNotifications);
    }, [setHasUnread, hasUnreadNotifications]);

    useEffect(() => {
        trackUserNotifications({
            stepInteractionName: 'unviewed notifications red dot shown',
            trigger: notificationsCount.toString(),
        });
    }, [notificationsCount]);

    if (!hasUnreadNotifications) {
        return null;
    }

    return (
        <div
            className={classnames(
                styles.unviewedCount,
                dibsCss.sassyFontBodySizeXxSmall,
                dibsCss.bgAlertprimary,
                dibsCss.roundedFull,
                dibsCss.h16px,
                dibsCss.minW16px,
                dibsCss.textWhite,
                dibsCss.flex,
                dibsCss.itemsCenter,
                dibsCss.justifyCenter,
                dibsCss.absolute,
                { [styles.isMobile]: isMobile }
            )}
        >
            {notificationsCount >= MAX_NOTIFICATIONS_COUNT
                ? `${MAX_NOTIFICATIONS_COUNT}+`
                : notificationsCount}
        </div>
    );
};

const UserNotificationsIcon: FC<{
    isMobile?: boolean;
    queryRef: PreloadedQuery<UserNotificationsQuery> | null | undefined;
    onClick: () => void;
    isOpen: boolean;
}> = ({ isMobile, queryRef, onClick, isOpen }) => {
    const htmlId = useId();
    const intl = useIntl();

    const [hasUnread, setHasUnread] = useState(false);

    return (
        <Link
            onClick={onClick}
            ariaLabel={intl.formatMessage(messages.label)}
            ariaDescribedBy={hasUnread ? htmlId : undefined}
            ariaExpanded={isOpen}
            ariaHasPopup
            dataTn={`notifications-link${hasUnread ? '-unread' : ''}`}
        >
            <div
                className={classnames(dibsCss.relative, dibsCss.flex, dibsCss.itemsCenter, {
                    [dibsCss.mxXsmall]: !isMobile,
                    [dibsCss.mxSmall]: isMobile,
                })}
            >
                <Suspense fallback={null}>
                    {queryRef && (
                        <NotificationsBadge
                            queryRef={queryRef}
                            isMobile={isMobile}
                            setHasUnread={setHasUnread}
                        />
                    )}
                </Suspense>
                {hasUnread && htmlId && (
                    <span id={htmlId} className={dibsCss.hidden}>
                        {intl.formatMessage(messages.description)}
                    </span>
                )}
                <AlertBellIcon
                    className={classnames(dibsCss.hSmall, dibsCss.wSmall, {
                        [styles.mobileIcon]: isMobile,
                    })}
                />
            </div>
        </Link>
    );
};

export { UserNotificationsIcon };
